/**
 * Parse an AxSelector wire-JSON string into its display-label fields.
 *
 * The note-push flow stores each field target as an AxSelector serialized to a
 * JSON string. Two leaf surfaces (the verification panel, the approval card)
 * turn that opaque string into a human label. Both parse it defensively — a
 * non-JSON string is a raw selector shown as-is — so the parse lives here once;
 * each caller keeps its own fallback chain (candidate lookup / `role` fallback).
 *
 * Returns ``undefined`` when the string is not JSON. Only the label-bearing
 * fields are modelled, each normalized to a non-empty string or ``undefined``.
 */
export const parseAxSelector = (
  selector: string
): { name?: string; role?: string } | undefined => {
  try {
    const parsed = JSON.parse(selector) as { name?: unknown; role?: unknown };
    return {
      name: typeof parsed.name === 'string' && parsed.name ? parsed.name : undefined,
      role: typeof parsed.role === 'string' && parsed.role ? parsed.role : undefined,
    };
  } catch {
    return undefined;
  }
};

/**
 * Human label for a selector: the matching discovered field's label when one
 * exists, else the selector's own `name`, else the raw selector. Shared by the
 * verification panel and the note-section mapping chip so a stored target reads
 * even before on-screen discovery finds a matching field.
 */
export const describeSelector = (
  selector: string,
  candidates: readonly { label: string; selector: string }[]
): string => {
  const match = candidates.find((candidate) => candidate.selector === selector);
  if (match) return match.label;
  return parseAxSelector(selector)?.name ?? selector;
};
